import { apiCall } from "./service";
import {
  adaptCareWatchSignal,
  adaptCareWatchSignals,
  adaptCareWatchLiveRow,
  adaptCareWatchLiveRows,
} from "pages/CareWatch/utils/signalAdapter";
import {
  URL_CARE_WATCH_ACKNOWLEDGE,
  URL_CARE_WATCH_RISK,
  URL_CARE_WATCH_OVERVIEW,
  URL_CARE_WATCH_SIGNAL,
  URL_CARE_WATCH_PROMOTE,
  URL_CARE_WATCH_SIGNALS,
  URL_CARE_WATCH_LIVE,
  URL_CARE_WATCH_LIVE_OVERVIEW,
} from "./URL";

const token = true;

/**
 * Care Watch — signals produced by AI timesheet analysis.
 *
 * The list is driven from ai_timesheet_anomalies server-side, so only
 * timesheets that have actually been analysed (manually or automatically)
 * appear. Rows are adapted to the dashboard's shape in signalAdapter.
 */
const getCareWatchList = async (filters = {}) => {
  // POST with the filters in the body, as `bodyParams` — which apiCall wraps
  // as { filter: … }, the same envelope the incident listing uses.
  //
  // Not `params`: apiCall appends known keys (status, search, …) to the URL as
  // well, so the same filter would arrive twice — once in the query string and
  // once in the body — and the two could disagree.
  const response = await apiCall.post({
    url: URL_CARE_WATCH_SIGNALS,
    isAuthToken: token,
    bodyParams: filters,
    skipQueryParams: true,
  });

  const payload = response?.data;
  const rows = Array.isArray(payload) ? payload : payload?.items || [];

  return {
    data: adaptCareWatchSignals(rows),
    total: Array.isArray(payload) ? rows.length : Number(payload?.total ?? rows.length),
    page: Array.isArray(payload) ? null : Number(payload?.page || 0) || null,
    perPage: Array.isArray(payload) ? null : Number(payload?.per_page || 0) || null,
    serverPaged: !Array.isArray(payload),
    url: URL_CARE_WATCH_SIGNALS,
  };
};

/**
 * KPI tile counts, computed server-side from the same filters as the listing.
 * A GET with the filters as query params: it is a read, and the counts are
 * cacheable in a way the listing is not.
 */
const getCareWatchOverview = async (filters = {}) => {
  // The query string is built here rather than handed to apiCall as `params`.
  // apiCall forwards only an allow-list of known parameter names, and the date
  // range is not on it — passing the filters through would silently drop the
  // dates and leave the tiles counting the whole history.
  //
  // Arrays (category) are repeated as category[]= so PHP keeps every value.
  // URLSearchParams would otherwise join them into one comma string.
  const query = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== undefined && item !== null && item !== "") {
          query.append(`${key}[]`, String(item));
        }
      });
      return;
    }
    query.append(key, String(value));
  });

  const response = await apiCall.get({
    url: query.toString()
      ? `${URL_CARE_WATCH_OVERVIEW}?${query.toString()}`
      : URL_CARE_WATCH_OVERVIEW,
    isAuthToken: token,
    skipQueryParams: true,
  });

  return { data: response?.data || {}, url: URL_CARE_WATCH_OVERVIEW };
};

const acknowledgeCareWatchSignals = async (ids = []) =>
  apiCall.post({
    url: URL_CARE_WATCH_ACKNOWLEDGE,
    isAuthToken: token,
    params: { ids },
  });

const setCareWatchRisk = async ({ id, timesheetId, incidentKey, risk }) =>
  apiCall.post({
    url: URL_CARE_WATCH_RISK,
    isAuthToken: token,
    params: {
      id,
      timesheet_id: timesheetId,
      incident_key: incidentKey || "",
      risk,
    },
  });

const promoteCareWatchSignal = async (id) =>
  apiCall.post({
    url: URL_CARE_WATCH_PROMOTE,
    isAuthToken: token,
    params: { id },
  });

/**
 * One signal by timesheet uuid.
 *
 * Used by the incident form to rebuild its prefill from `?care_watch=<uuid>`:
 * a URL parameter survives a refresh and a shared link, which router state
 * does not.
 */
/**
 * One signal by timesheet uuid.
 *
 * A group shift can carry several incidents. `incidentKey` names which one —
 * without it the server returns the first, which is what the incident form
 * wants when it only has the `?care_watch=<uuid>` parameter to go on.
 */
const getCareWatchSignal = async (uuid, incidentKey = "", view = "") => {
  const params = new URLSearchParams();
  if (incidentKey) params.set("incident_key", incidentKey);
  // Missed documentation must be read as paperwork. Without this the detail
  // endpoint returns the incident headline, and the documentation check
  // tells the manager to file a hazard that is already created.
  if (view === "documentation" || view === "incident") {
    params.set("view", view);
  }
  const query = params.toString() ? `?${params.toString()}` : "";
  const response = await apiCall.get({
    url: `${URL_CARE_WATCH_SIGNAL}/${uuid}${query}`,
    isAuthToken: token,
    skipQueryParams: true,
  });

  const raw = response?.data || null;
  return { data: raw ? adaptCareWatchSignal(raw) : null };
};

/**
 * Care Watch Live — today's shift floor state.
 * GET endpoint, filters as query params.
 */
const getCareWatchLiveList = async (filters = {}) => {
  const query = new URLSearchParams(
    Object.entries(filters).filter(
      ([, value]) => value !== undefined && value !== null && value !== ""
    )
  ).toString();

  const response = await apiCall.get({
    url: query ? `${URL_CARE_WATCH_LIVE}?${query}` : URL_CARE_WATCH_LIVE,
    isAuthToken: token,
    skipQueryParams: true,
  });

  return {
    data: adaptCareWatchLiveRows(response?.data || []),
    url: URL_CARE_WATCH_LIVE,
  };
};

const getCareWatchLiveOverview = async (filters = {}) => {
  const query = new URLSearchParams(
    Object.entries(filters).filter(
      ([, value]) => value !== undefined && value !== null && value !== ""
    )
  ).toString();

  const response = await apiCall.get({
    url: query
      ? `${URL_CARE_WATCH_LIVE_OVERVIEW}?${query}`
      : URL_CARE_WATCH_LIVE_OVERVIEW,
    isAuthToken: token,
    skipQueryParams: true,
  });

  return { data: response?.data || {}, url: URL_CARE_WATCH_LIVE_OVERVIEW };
};

const getCareWatchLiveDetail = async (shiftId, workerId) => {
  const params = workerId ? `?worker_id=${workerId}` : "";
  const response = await apiCall.get({
    url: `${URL_CARE_WATCH_LIVE}/${shiftId}${params}`,
    isAuthToken: token,
    skipQueryParams: true,
  });
  const raw = response?.data || null;
  return { data: raw ? adaptCareWatchLiveRow(raw) : null };
};

export default {
  getCareWatchList,
  getCareWatchSignal,
  getCareWatchOverview,
  acknowledgeCareWatchSignals,
  setCareWatchRisk,
  promoteCareWatchSignal,
  getCareWatchLiveList,
  getCareWatchLiveOverview,
  getCareWatchLiveDetail,
};
